/* Bionobo quick-cart mutation and interaction wiring.
 * Adds request every rendered cart section with the mutation so Horizon can
 * settle the drawer, totals and count from one response. A single queue keeps
 * overlapping drawer and PDP mutations in server order. */
(function () {
  if (window.__bnQuickcartWired) return;
  window.__bnQuickcartWired = true;

  var mutationTail = Promise.resolve();
  var pendingMutations = 0;
  var walletInertBefore = typeof WeakMap === 'function' ? new WeakMap() : null;
  var activeMutations = typeof WeakMap === 'function' ? new WeakMap() : null;
  var cartReadPending = null;
  var cartReadGeneration = 0;
  var currentGenerationCart = null;
  var refreshPending = null;
  var needsRestore = false;
  var giftDiscountProblem = false;
  var giftNotice = '';
  var giftNoticeKey = '';
  var checkoutPending = null;
  var checkoutIntentPending = null;
  var checkoutAuthorized = false;
  var lastCartItems = [];
  var cartLineStateInitialized = false;
  var cartLineKeys = {};
  var discountCart = null;
  var discountNotice = null;
  var discountPending = false;

  function cartUrl(action) {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/';
    return root.replace(/\/?$/, '/') + (action === 'js' ? 'cart.js' : 'cart/' + action);
  }

  function lineIdentity(line) {
    var properties = line.properties || {};
    var entries = Object.keys(properties).sort().map(function (key) { return [key, properties[key]]; });
    var plan = line.selling_plan_allocation && line.selling_plan_allocation.selling_plan;
    return JSON.stringify([String(line.variant_id), plan && String(plan.id) || '', entries]);
  }

  function rememberCart(cart) {
    if (!cart || !Array.isArray(cart.items)) return cart;
    if (Number.isInteger(cart.item_count)) discountCart = cart;
    var previousByIdentity = {};
    var currentByIdentity = {};
    lastCartItems.forEach(function (line) {
      var identity = lineIdentity(line);
      (previousByIdentity[identity] || (previousByIdentity[identity] = [])).push(line);
    });
    cart.items.forEach(function (line) {
      var identity = lineIdentity(line);
      (currentByIdentity[identity] || (currentByIdentity[identity] = [])).push(line);
    });
    Object.keys(previousByIdentity).forEach(function (identity) {
      var previous = previousByIdentity[identity];
      var current = currentByIdentity[identity];
      if (previous.length === 1 && current && current.length === 1 && previous[0].key && current[0].key) {
        cartLineKeys[previous[0].key] = current[0].key;
      }
    });
    cart.items.forEach(function (line) { if (line.key) cartLineKeys[line.key] = line.key; });
    lastCartItems = cart.items.slice();
    cartLineStateInitialized = true;
    return cart;
  }

  function initializeCartLineState() {
    if (cartLineStateInitialized) return;
    var node = document.querySelector('script[data-bn-cart-line-state]');
    var state;
    try { state = node && JSON.parse(node.textContent); } catch (_) { return; }
    if (!state || !Array.isArray(state.items)) return;
    var keys = new Set();
    var valid = state.items.every(function (line) {
      if (!line || typeof line.key !== 'string' || !line.key || keys.has(line.key)
        || !Number.isInteger(Number(line.variant_id)) || Number(line.variant_id) <= 0) return false;
      if (line.properties && (typeof line.properties !== 'object'
        || Array.isArray(line.properties) && line.properties.length)) return false;
      var plan = line.selling_plan_allocation && line.selling_plan_allocation.selling_plan;
      if (line.selling_plan_allocation && (!plan || !Number.isInteger(Number(plan.id)) || Number(plan.id) <= 0)) return false;
      keys.add(line.key);
      return true;
    });
    // This snapshot seeds exact identities only; it cannot authorize prices or checkout.
    if (valid) rememberCart({ items: state.items });
  }

  function resolveLineKey(key) {
    var visited = {};
    while (cartLineKeys[key] && cartLineKeys[key] !== key && !visited[key]) {
      visited[key] = true;
      key = cartLineKeys[key];
    }
    return key;
  }

  function readCart() {
    if (cartReadPending) return cartReadPending;
    var generation = cartReadGeneration;
    var redirectOldRead = function () { return currentGenerationCart || readCart(); };
    var pending = fetch(cartUrl('js'), { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(function (response) {
        if (!response.ok) throw new Error('The cart could not be read. Please try again.');
        return response.json();
      })
      .then(function (cart) {
        if (generation !== cartReadGeneration) return redirectOldRead();
        if (!cart || !Array.isArray(cart.items)) throw new Error('The cart could not be read. Please try again.');
        currentGenerationCart = rememberCart(cart);
        return currentGenerationCart;
      }).catch(function (error) {
        if (generation !== cartReadGeneration) return redirectOldRead();
        throw error;
      });
    cartReadPending = pending;
    var clear = function () { if (cartReadPending === pending) cartReadPending = null; };
    pending.then(clear, clear);
    return pending;
  }

  function invalidateCartRead(cart) {
    cartReadGeneration += 1;
    cartReadPending = null;
    currentGenerationCart = cart && Number.isInteger(cart.item_count) && Array.isArray(cart.items) ? rememberCart(cart) : null;
  }

  function settleCartEvent(event) {
    var data = event && event.detail && event.detail.data;
    return Promise.all((data && data.sectionSettlements || []).concat(data && data.quantitySettlements || [])).finally(renderGiftNotice);
  }

  function giftStateFromResponse(body) {
    var sections = body && body.sections;
    if (sections && typeof DOMParser === 'function') {
      var keys = Object.keys(sections);
      for (var index = 0; index < keys.length; index += 1) {
        if (typeof sections[keys[index]] !== 'string') continue;
        var fragment = new DOMParser().parseFromString(sections[keys[index]], 'text/html');
        var stateNode = fragment.querySelector && fragment.querySelector('script[data-bn-gift-state]');
        if (stateNode) {
          try { return JSON.parse(stateNode.textContent); } catch (_) {}
        }
      }
    }
    var node = document.querySelector('script[data-bn-gift-state]');
    try { return node ? JSON.parse(node.textContent) : null; } catch (_) { return null; }
  }

  function configuredGiftState(state) {
    var node = document.querySelector('script[data-bn-gift-config]');
    if (!node || !state) return state;
    var config;
    try { config = JSON.parse(node.textContent); } catch (_) { config = {}; }
    var tiers = Array.isArray(config.tiers) ? config.tiers : [];
    return Object.assign({}, state, {
      tiers: tiers,
      gifts: (state.gifts || []).map(function (gift) {
        var tier = tiers.find(function (candidate) { return String(candidate.tier) === String(gift.tier); });
        return Object.assign({}, gift, { threshold: tier ? Number(tier.threshold) : 0 });
      }),
    });
  }

  function showGiftNotice(state, messageKey) {
    giftNoticeKey = messageKey;
    giftNotice = state && state.messages && state.messages[messageKey] || '';
    renderGiftNotice();
  }

  function renderGiftNotice() {
    gateAcceleratedCheckout();
    renderDiscountState();
    var status = document.querySelector('[data-bn-gift-status]');
    if (!status) return;
    status.textContent = giftNotice;
    if (status.classList && status.classList.toggle) status.classList.toggle('is-error', giftDiscountProblem);
  }

  function gateAcceleratedCheckout() {
    var blocked = pendingMutations > 0 || needsRestore || giftDiscountProblem;
    document.querySelectorAll('.additional-checkout-buttons').forEach(function (wrapper) {
      if (blocked) {
        if (walletInertBefore && !walletInertBefore.has(wrapper)) walletInertBefore.set(wrapper, wrapper.inert === true);
        wrapper.inert = true;
        if (wrapper.setAttribute) wrapper.setAttribute('aria-busy', 'true');
      } else if (!walletInertBefore || walletInertBefore.has(wrapper)) {
        wrapper.inert = walletInertBefore ? walletInertBefore.get(wrapper) : false;
        if (walletInertBefore) walletInertBefore.delete(wrapper);
        if (wrapper.removeAttribute) wrapper.removeAttribute('aria-busy');
      }
    });
  }

  function requireFreshCart() {
    needsRestore = true;
    gateAcceleratedCheckout();
  }

  function giftEligibleCents(cart) {
    return cart.items.reduce(function (sum, line) {
      if (isMarkedGift(line)) return sum;
      if (Number(line.line_level_total_discount || 0) !== 0 || Number(line.final_line_price) <= 0) return sum;
      var amount = Number(line.original_line_price);
      return Number.isInteger(amount) && amount > 0 ? sum + amount : sum;
    }, 0);
  }

  function readGiftMutation(response, plan) {
    return response.json().catch(function () {
      throw new Error('The gift update could not be confirmed.');
    }).then(function (body) {
      if (!response.ok || body.status || body.errors || !Array.isArray(body.items) || !Number.isInteger(body.item_count)) {
        throw new Error(body.description || body.message || 'The gift update could not be confirmed.');
      }
      invalidateCartRead(body);
      return settleRequiredSections(body, plan);
    });
  }

  function giftsEnabled() {
    var component = document.querySelector('[data-bn-gifts-enabled]');
    if (component) return component.getAttribute('data-bn-gifts-enabled') === 'true';
    var configNode = document.querySelector('script[data-bn-gift-config]');
    try { return Boolean(configNode && JSON.parse(configNode.textContent).enabled === true); } catch (_) { return false; }
  }

  function isMarkedGift(line) {
    var properties = line && line.properties;
    return Boolean(properties && (String(properties._bn_gift) === '1'
      || /^(300|650|999)$/.test(String(properties._bn_gift_tier || ''))));
  }

  function reconcileGifts(body, options) {
    options = options || {};
    var enabled = giftsEnabled();
    var fullCart = body && Number.isInteger(body.item_count) && Array.isArray(body.items);
    var knownLines = fullCart ? body.items : lastCartItems;
    if (!enabled && !knownLines.some(isMarkedGift)) {
      giftDiscountProblem = false;
      giftNotice = '';
      giftNoticeKey = '';
      return Promise.resolve(fullCart ? rememberCart(body) : body);
    }
    var state = enabled ? configuredGiftState(options.state || giftStateFromResponse(body)) : null;
    var plan = cartSectionPlan();
    return (fullCart ? Promise.resolve(body) : readCart()).then(function (cart) {
      rememberCart(cart);
      var updates = {};
      var gifts = cart.items.filter(isMarkedGift);
      if (enabled) {
        if (!state || !Array.isArray(state.tiers)) throw new Error('The gift eligibility could not be confirmed.');
        var eligibleCents = giftEligibleCents(cart);
        var thresholds = {};
        state.tiers.forEach(function (tier) { thresholds[String(tier.tier)] = Number(tier.threshold); });
        var kept = false;
        gifts.sort(function (left, right) { return Number(left.final_line_price > 0) - Number(right.final_line_price > 0); });
        gifts.forEach(function (line) {
          var threshold = thresholds[String(line.properties._bn_gift_tier)];
          if (!Number.isInteger(threshold) || threshold <= 0 || eligibleCents < threshold || kept) updates[line.key] = 0;
          else { kept = true; if (line.quantity > 1) updates[line.key] = 1; }
        });
      } else {
        gifts.forEach(function (line) { updates[line.key] = 0; });
        giftDiscountProblem = false;
        giftNotice = '';
        giftNoticeKey = '';
      }
      if (!Object.keys(updates).length) return cart;
      return fetch(cartUrl('update.js'), {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: updates, sections: plan.requested.join(','), sections_url: window.location.pathname }),
      }).then(function (response) { return readGiftMutation(response, plan); }).then(function (updated) {
        Object.keys(updates).forEach(function (key) {
          var line = updated.items.find(function (item) { return item.key === key; });
          if (updates[key] === 0 && line || updates[key] > 0 && (!line || line.quantity !== updates[key])) {
            throw new Error('The gift update could not be confirmed.');
          }
        });
        if (enabled) showGiftNotice(state, 'removed');
        return updated;
      });
    }).then(function (cart) {
      giftDiscountProblem = enabled && cart.items.some(function (line) { return isMarkedGift(line) && Number(line.final_line_price) > 0; });
      if (giftDiscountProblem) showGiftNotice(state, 'discountProblem');
      else renderGiftNotice();
      return rememberCart(Object.assign({}, body || {}, cart, { sections: cart.sections || body && body.sections || {} }));
    }).catch(function (error) {
      giftDiscountProblem = enabled;
      error.giftReconciliationFailed = enabled;
      if (enabled) showGiftNotice(state, 'updateFailed');
      throw error;
    });
  }

  function prepareCheckout() {
    if (checkoutPending) return checkoutPending;
    if (needsRestore) return refreshCart().then(prepareCheckout);
    var pending = enqueueMutation(function () {
      invalidateCartRead();
      return readCart().then(function (cart) { return reconcileGifts(cart); }).then(function (cart) {
        var settlement = cart.sections && Object.keys(cart.sections).length
          ? publishCartResponse(cart, { source: 'cart-gift-reconciliation', mutation: 'update' }) : Promise.resolve();
        return settlement.then(function () {
          renderGiftNotice();
          if (giftDiscountProblem) throw new Error(giftNotice || 'The gift discount could not be confirmed.');
          return cart;
        });
      }).catch(function (error) {
        giftDiscountProblem = giftsEnabled();
        if (giftsEnabled() && error.message !== giftNotice) showGiftNotice(giftStateFromResponse(), 'updateFailed');
        throw error;
      });
    });
    checkoutPending = pending;
    var clear = function () { if (checkoutPending === pending) checkoutPending = null; };
    pending.then(clear, clear);
    return pending;
  }

  function prepareLineMutation(lineKey) {
    if (!lineKey || lastCartItems.some(function (line) { return line.key === resolveLineKey(lineKey); })) return Promise.resolve();
    invalidateCartRead();
    return readCart().then(function (cart) {
      if (!cart.items.some(function (line) { return line.key === resolveLineKey(lineKey); })) {
        throw new Error('The cart line could not be identified. Please refresh your cart.');
      }
    }).catch(function (error) {
      requireFreshCart();
      throw error;
    });
  }

  function prepareCartMutation(options) {
    initializeCartLineState();
    if (!giftsEnabled() && lastCartItems.some(isMarkedGift)) {
      invalidateCartRead();
      return readCart().then(function (cart) { return reconcileGifts(cart); }).then(function (cart) {
        return cart.sections && Object.keys(cart.sections).length
          ? publishCartResponse(cart, { source: 'cart-gift-reconciliation', mutation: 'update' }) : null;
      }).then(function () { return prepareLineMutation(options && options.lineKey); });
    }
    return prepareLineMutation(options && options.lineKey);
  }

  function continueCheckout(action) {
    if (checkoutIntentPending) return;
    var pending = prepareCheckout().then(action);
    checkoutIntentPending = pending;
    var clear = function () { if (checkoutIntentPending === pending) checkoutIntentPending = null; };
    pending.then(clear, function (error) {
      clear();
      console.error('[bn-quickcart checkout]', error);
    });
  }

  function submitCurrentCheckout(form, submitter) {
    var currentForm = form;
    if (form.isConnected === false && form.id && document.getElementById) currentForm = document.getElementById(form.id);
    if (!currentForm || currentForm.isConnected === false) throw new Error('The cart form could not be refreshed. Please try checkout again.');
    if (currentForm !== form || submitter.isConnected === false) submitter = currentForm.querySelector('[name="checkout"]');
    if (!submitter || submitter.name !== 'checkout') throw new Error('The checkout button could not be refreshed. Please try checkout again.');
    checkoutAuthorized = true;
    try { currentForm.requestSubmit(submitter); } finally { checkoutAuthorized = false; }
  }

  function enqueueMutation(operation) {
    pendingMutations += 1;
    gateAcceleratedCheckout();
    var current = mutationTail.catch(function () {}).then(operation).finally(function () {
      pendingMutations -= 1;
      gateAcceleratedCheckout();
    });
    mutationTail = current.catch(function () {});
    return current;
  }

  function enqueueOnce(owner, operation) {
    if (!activeMutations || !owner) return enqueueMutation(operation);
    var active = activeMutations.get(owner);
    if (active) return active;
    var pending = enqueueMutation(operation);
    activeMutations.set(owner, pending);
    var clear = function () {
      if (activeMutations.get(owner) === pending) activeMutations.delete(owner);
    };
    pending.then(clear, clear);
    return pending;
  }

  function isRequiredCartSection(component) {
    if (component.closest && component.closest('cart-drawer-component')) return true;
    if (component.hidden === true || (component.getAttribute && component.getAttribute('aria-hidden') === 'true')) return false;
    if (typeof component.getClientRects === 'function') return component.getClientRects().length > 0;
    return true;
  }

  function cartSectionPlan() {
    var components = Array.prototype.slice.call(document.querySelectorAll('cart-items-component'));
    components.sort(function (left, right) {
      return Number(isRequiredCartSection(right)) - Number(isRequiredCartSection(left));
    });

    var seen = {};
    var requested = [];
    var required = [];
    components.forEach(function (component) {
      var id = component.dataset && component.dataset.sectionId;
      if (!id || seen[id] || requested.length >= 5) return;
      seen[id] = true;
      requested.push(id);
      if (isRequiredCartSection(component)) required.push(id);
    });
    return { requested: requested, required: required };
  }

  function cartSectionIds() {
    return cartSectionPlan().requested;
  }

  function cartItemCountFromSections(sections) {
    if (!sections) return null;
    var html = Object.keys(sections).map(function (key) { return sections[key] || ''; }).join('\n');
    var match = html.match(/ref=["']cartItemCount["'][^>]*>\s*(\d+)/i);
    return match ? Number(match[1]) : null;
  }

  function publishCartResponse(body, options) {
    options = options || {};
    var totalItemCount = body && Number.isInteger(body.item_count) ? body.item_count : cartItemCountFromSections(body && body.sections);
    var addedItemCount = Number(options.itemCount) || 0;
    var event = new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        // /cart/add.js returns the added lines, not the full cart. Do not hand
        // that partial object to product forms as though it contained every
        // cart line; they will fetch the full cart when they need it.
        resource: body && Number.isInteger(body.item_count) && Array.isArray(body.items) ? body : {},
        sourceId: options.sourceId || '',
        data: {
          source: totalItemCount === null ? 'product-form-component' : (options.source || 'bionobo-cart'),
          origin: options.source || 'bionobo-cart',
          mutation: options.mutation || 'update',
          confirmedAdd: options.confirmedAdd === true,
          itemCount: totalItemCount === null ? addedItemCount : totalItemCount,
          itemCountIsTotal: totalItemCount !== null,
          productId: options.productId,
          variantId: options.variantId,
          sections: body && body.sections,
          sectionSettlements: [],
          quantitySettlements: [],
        },
      },
    });
    document.dispatchEvent(event);
    return settleCartEvent(event).then(function () { renderGiftNotice(); });
  }

  function addedItemsMatch(items, expectedItems) {
    if (!Array.isArray(items) || !items.length || !expectedItems.length) return false;
    var actualQuantities = {};
    var expectedQuantities = {};

    items.forEach(function (line) {
      var id = line && (line.variant_id != null ? line.variant_id : line.id);
      var quantity = Number(line && line.quantity);
      var key = id == null ? '' : String(id).trim();
      if (!key || !Number.isInteger(quantity) || quantity <= 0) return;
      actualQuantities[key] = (actualQuantities[key] || 0) + quantity;
    });
    var validExpectedItems = expectedItems.every(function (line) {
      var id = line && line.id;
      var quantity = Number(line && line.quantity);
      var key = id == null ? '' : String(id).trim();
      if (!key || !Number.isInteger(quantity) || quantity <= 0) return false;
      expectedQuantities[key] = (expectedQuantities[key] || 0) + quantity;
      return true;
    });

    var expectedIds = Object.keys(expectedQuantities);
    return validExpectedItems && expectedIds.length > 0 && expectedIds.every(function (id) {
      // Shopify can return the resulting line quantity for a warm cart. The
      // response still proves the add when every requested aggregate is met.
      return actualQuantities[id] >= expectedQuantities[id];
    });
  }

  function parseCartResponse(response, fallbackMessage, expectedItems) {
    return response.json().catch(function () {
      var error = new Error('The cart response could not be read.');
      error.cartMutationUncertain = true;
      throw error;
    }).then(function (body) {
      var hasMutationEvidence = addedItemsMatch(body && body.items, expectedItems);
      if ((!response.ok || (body && body.status)) && !hasMutationEvidence) {
        var error = new Error((body && (body.description || body.message)) || fallbackMessage);
        error.cartMutationUncertain = !response.ok && response.status !== 422;
        throw error;
      }
      if (!hasMutationEvidence) {
        var evidenceError = new Error('The cart response did not confirm the added item.');
        evidenceError.cartMutationUncertain = true;
        throw evidenceError;
      }
      return body;
    });
  }

  function hasSectionMarkup(sections, id) {
    if (!sections || typeof sections[id] !== 'string' || !sections[id].trim()) return false;
    var fragment = new DOMParser().parseFromString(sections[id], 'text/html');
    return Boolean(fragment.getElementById('shopify-section-' + id));
  }

  function historySectionUrl(parameter, ids) {
    var url = new URL(window.location.href);
    url.searchParams.delete('sections');
    url.searchParams.delete('section_id');
    url.searchParams.set(parameter, ids);
    return url.pathname + url.search;
  }

  function recoverHistorySections(sections, ids) {
    return Promise.all(ids.map(function (id) {
      return fetch(historySectionUrl('section_id', id), {
        headers: { Accept: 'text/html' }, cache: 'no-store',
      }).then(function (response) {
        if (!response.ok) throw new Error('The cart changed, but its sections could not be refreshed.');
        return response.text();
      }).then(function (html) {
        var recovered = {};
        recovered[id] = html;
        if (!hasSectionMarkup(recovered, id)) throw new Error('The cart changed, but a required section could not be refreshed.');
        return recovered;
      });
    })).then(function (recovered) {
      return Object.assign.apply(Object, [{}, sections || {}].concat(recovered));
    });
  }

  function settleRequiredSections(body, plan, options) {
    options = options || {};
    var missing = plan.required.filter(function (id) {
      return !hasSectionMarkup(body && body.sections, id);
    });
    if (!missing.length) return Promise.resolve(body);

    var separator = window.location.pathname.indexOf('?') === -1 ? '?' : '&';
    var url = options.recoverHistorySections
      ? historySectionUrl('sections', missing.join(','))
      : window.location.pathname + separator + 'sections=' + encodeURIComponent(missing.join(','));
    return fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(function (response) {
        if (!response.ok) throw new Error('The cart changed, but its sections could not be refreshed.');
        return response.json();
      })
      .then(function (sections) {
        var invalid = missing.filter(function (id) {
          return !hasSectionMarkup(sections, id);
        });
        if (!invalid.length) return sections;
        if (options.recoverHistorySections) return recoverHistorySections(sections, invalid);
        throw new Error('The cart changed, but a required section could not be refreshed.');
      })
      .then(function (sections) {
        body.sections = Object.assign({}, body.sections || {}, sections);
        return body;
      });
  }

  function requestAdd(item, options) {
    options = options || {};
    var priorResponse = null;
    var addRequested = false;
    var sectionPlan = cartSectionPlan();
    return Promise.resolve()
      .then(function () {
        return prepareCartMutation();
      })
      .then(function () {
        return typeof options.beforeRequest === 'function' ? options.beforeRequest() : null;
      })
      .then(function (response) {
        priorResponse = response;
        var payload = {
          items: [item],
          sections: sectionPlan.requested.join(','),
          sections_url: window.location.pathname,
        };
        addRequested = true;
        return fetch(cartUrl('add.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        });
      })
      .then(function (response) {
        return parseCartResponse(
          response,
          options.errorMessage || 'We could not add this item to your cart. Please try again.',
          [item]
        );
      })
      .then(function (body) {
        invalidateCartRead(body);
        return settleRequiredSections(body, sectionPlan);
      })
      .then(function (body) { return reconcileGifts(body); })
      .then(function (body) {
        if (typeof options.beforePublish === 'function') {
          try {
            options.beforePublish(body);
          } catch (error) {
            console.error('[bn-quickcart beforePublish]', error);
          }
        }
        return publishCartResponse(body, {
          source: options.source,
          sourceId: options.sourceId,
          mutation: 'add',
          confirmedAdd: true,
          itemCount: item.quantity,
          productId: options.productId,
          variantId: item.id,
        }).then(function () { return body; });
      })
      .catch(function (error) {
        invalidateCartRead();
        if (addRequested && error.cartMutationUncertain !== false) error.cartMutationUncertain = true;
        var authoritativeRefresh = error.cartMutationUncertain
          ? settleRequiredSections({ sections: {} }, sectionPlan).then(function (body) { return reconcileGifts(body); }).catch(function () { return null; })
          : Promise.resolve(null);
        return authoritativeRefresh.then(function (recoveredResponse) {
          if (error.cartMutationUncertain && !recoveredResponse) requireFreshCart();
          // A failed response can still follow a completed cart mutation. In
          // that case, refresh the cart without claiming add success or opening
          // the drawer. A confirmed gift precursor remains authoritative too.
          var authoritativeResponse = recoveredResponse || (priorResponse && priorResponse.sections ? priorResponse : null);
          if (authoritativeResponse) {
            return publishCartResponse(authoritativeResponse, {
              source: options.source,
              sourceId: options.sourceId,
              mutation: 'update',
            }).then(function () { throw error; });
          }
          throw error;
        });
      });
  }

  window.BionoboCartMutations = {
    add: function (item, options) {
      if (options && options.source === 'cart-gift' && !giftsEnabled()) return Promise.reject(new Error('Gift rewards are currently unavailable.'));
      return enqueueMutation(function () { return requestAdd(item, options); });
    },
    enqueue: enqueueMutation,
    enqueueOnce: enqueueOnce,
    applyDiscount: function (code) { return changeDiscount(code, false); },
    removeDiscount: function (code) { return changeDiscount(code, true); },
    read: readCart,
    invalidateRead: invalidateCartRead,
    requireFresh: requireFreshCart,
    resolveLineKey: resolveLineKey,
    countFromSections: cartItemCountFromSections,
    settleEvent: settleCartEvent,
    reconcile: reconcileGifts,
    prepareCheckout: prepareCheckout,
    prepareMutation: prepareCartMutation,
    publish: publishCartResponse,
    restoreNotice: renderGiftNotice,
    ensureCurrent: function () {
      return needsRestore ? refreshCart() : (refreshPending || Promise.resolve());
    },
    refresh: refreshCart,
    createSectionRequest: function () {
      var plan = cartSectionPlan();
      return {
        ids: plan.requested.slice(),
        settle: function (body) { return settleRequiredSections(body, plan); },
        recover: function (options) {
          options = options || {};
          invalidateCartRead();
          return settleRequiredSections({ sections: {} }, plan).then(function (body) {
            return reconcileGifts(body);
          }).then(function (body) {
            return publishCartResponse(body, {
              source: options.source || 'product-form-component',
              sourceId: options.sourceId,
              mutation: 'update',
            }).then(function () { return body; });
          }).catch(function (error) {
            if (options.uncertain !== false) requireFreshCart();
            throw error;
          });
        },
      };
    },
  };

  function discountApplication(allocation) {
    return allocation && (allocation.discount_application || allocation);
  }

  function discountCodesFromCart(cart) {
    var codes = [];
    if (cart && Array.isArray(cart.discount_codes)) {
      codes = cart.discount_codes.filter(function (entry) { return entry && entry.applicable === true; })
        .map(function (entry) { return String(entry.code || '').trim(); });
    } else {
      (cart && cart.cart_level_discount_applications || []).forEach(function (allocation) {
        var application = discountApplication(allocation);
        if (application && application.type === 'discount_code') codes.push(String(application.title || '').trim());
      });
      (cart && cart.items || []).forEach(function (item) {
        (item.line_level_discount_allocations || []).forEach(function (allocation) {
          var application = discountApplication(allocation);
          if (application && application.type === 'discount_code') codes.push(String(application.title || '').trim());
        });
      });
    }
    var seen = {};
    return codes.filter(function (code) {
      var key = code.toLowerCase();
      if (!code || seen[key]) return false;
      seen[key] = true;
      return true;
    });
  }

  function hasMerchandiseDiscount(cart, code) {
    var matches = function (allocation) {
      var application = discountApplication(allocation);
      var amount = allocation && allocation.amount != null ? allocation.amount : application && application.total_allocated_amount;
      return application && application.type === 'discount_code' && application.target_type !== 'shipping_line' && Number(amount) > 0
        && String(application.title || '').toLowerCase() === code.toLowerCase();
    };
    if ((cart.cart_level_discount_applications || []).some(matches)) return true;
    return cart.items.some(function (item) {
      return (item.line_level_discount_allocations || []).some(matches);
    });
  }

  function changeDiscount(code, remove) {
    code = String(code || '').trim();
    if (!code || code.indexOf(',') !== -1) return Promise.reject(new Error('Enter one discount code.'));
    return enqueueMutation(function () { return requestDiscount(code, remove); });
  }

  function requestDiscount(code, remove) {
    var plan;
    var requested = false;
    return prepareCartMutation().then(function () {
      invalidateCartRead();
      return readCart();
    }).then(function (cart) {
      var codes = discountCodesFromCart(cart);
      codes = codes.filter(function (existing) { return existing.toLowerCase() !== code.toLowerCase(); });
      if (!remove) codes.push(code);
      plan = cartSectionPlan();
      requested = true;
      return fetch(cartUrl('update.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: codes.join(','), sections: plan.requested.join(','), sections_url: window.location.pathname }),
      });
    }).then(function (response) {
      return response.json().then(function (cart) {
        if (!response.ok || !cart || cart.status || cart.errors || !Array.isArray(cart.items)
          || !Number.isInteger(cart.item_count) || !Number.isInteger(cart.total_price)
          || !Array.isArray(cart.discount_codes)) {
          throw new Error(cart && (cart.description || cart.message) || 'The discount update could not be confirmed.');
        }
        invalidateCartRead(cart);
        return settleRequiredSections(cart, plan);
      });
    }).then(function (cart) { return reconcileGifts(cart); }).then(function (cart) {
      var applied = cart.discount_codes.find(function (entry) { return String(entry.code).toLowerCase() === code.toLowerCase(); });
      if (remove && applied && applied.applicable === true) throw new Error('The discount removal could not be confirmed.');
      var status = remove ? 'removed' : (!applied || applied.applicable !== true ? 'invalid'
        : (hasMerchandiseDiscount(cart, code) ? 'applied' : 'shipping'));
      return publishCartResponse(cart, { source: 'cart-discount', mutation: 'update' }).then(function () {
        needsRestore = false;
        return { cart: cart, code: applied && applied.code || code, status: status };
      });
    }).catch(function (error) {
      if (!requested) throw error;
      invalidateCartRead();
      return readCart().then(function (cart) { return settleRequiredSections(cart, plan); })
        .then(function (cart) { return publishCartResponse(cart, { source: 'cart-discount-recovery', mutation: 'update' }); })
        .catch(function () { requireFreshCart(); })
        .then(function () { throw error; });
    });
  }

  function discountMessage(component, kind, code) {
    return (component.getAttribute('data-bn-discount-' + kind + '-message') || '').split('__CODE__').join(code || '');
  }

  function renderDiscountState() {
    var codes = discountCart ? discountCodesFromCart(discountCart) : [];
    if (!discountPending && discountCart && discountNotice
      && (discountNotice.kind === 'applied' || discountNotice.kind === 'shipping')) {
      var stillApplicable = codes.some(function (code) { return code.toLowerCase() === discountNotice.code.toLowerCase(); });
      discountNotice.kind = !stillApplicable ? 'invalid'
        : (hasMerchandiseDiscount(discountCart, discountNotice.code) ? 'applied' : 'shipping');
    }
    document.querySelectorAll('[data-bn-discount]').forEach(function (component) {
      component.setAttribute('aria-busy', discountPending ? 'true' : 'false');
      component.querySelectorAll('input, button').forEach(function (control) { control.disabled = discountPending; });
      var status = component.querySelector('[data-bn-discount-status]');
      var input = component.querySelector('[data-bn-discount-input]');
      if (discountNotice && status) {
        status.textContent = discountMessage(component, discountNotice.kind, discountNotice.code);
        var isError = discountNotice.kind === 'invalid' || discountNotice.kind === 'error' || discountNotice.kind === 'empty';
        status.classList.toggle('is-error', isError);
        if (input) input.setAttribute('aria-invalid', isError ? 'true' : 'false');
      }
      if (!discountCart) return;
      var list = component.querySelector('[data-bn-discount-codes]');
      if (list && typeof document.createElement === 'function') {
        list.replaceChildren();
        codes.forEach(function (code) {
          var pill = document.createElement('li');
          pill.className = 'bnc-discount__pill';
          var label = document.createElement('span');
          label.textContent = code;
          var button = document.createElement('button');
          button.type = 'button';
          button.className = 'bnc-discount__remove';
          button.setAttribute('data-bn-discount-remove', code);
          button.setAttribute('aria-label', discountMessage(component, 'remove', code));
          button.textContent = '×';
          button.disabled = discountPending;
          pill.append(label, button);
          list.append(pill);
        });
      }
      var shipping = codes.filter(function (code) { return !hasMerchandiseDiscount(discountCart, code); });
      var note = component.querySelector('[data-bn-discount-shipping]');
      if (note) {
        note.hidden = !shipping.length || Boolean(discountNotice && discountNotice.kind === 'shipping');
        note.textContent = shipping.length ? discountMessage(component, 'shipping', shipping.join(', ')) : '';
      }
    });
  }

  function performDiscountAction(component, code, remove) {
    if (discountPending) return;
    var submittedInput = component.querySelector('[data-bn-discount-input]');
    var inputId = submittedInput && submittedInput.id;
    function currentInput() {
      return inputId ? document.getElementById(inputId) : component.querySelector('[data-bn-discount-input]');
    }
    code = String(code || '').trim();
    if (!code || code.indexOf(',') !== -1) {
      discountNotice = { kind: 'empty', code: code };
      renderDiscountState();
      return;
    }
    discountPending = true;
    discountNotice = null;
    var status = component.querySelector('[data-bn-discount-status]');
    if (status) status.textContent = '';
    renderDiscountState();
    var operation = remove ? window.BionoboCartMutations.removeDiscount(code) : window.BionoboCartMutations.applyDiscount(code);
    operation.then(function (result) {
      discountNotice = { kind: result.status, code: String(result.code) };
      var current = currentInput();
      if (current) current.value = result.status === 'invalid' ? code : '';
    }).catch(function (error) {
      discountNotice = { kind: 'error', code: code };
      console.error('[bn-quickcart discount]', error);
    }).finally(function () {
      discountPending = false;
      renderDiscountState();
      var current = currentInput();
      if (current && typeof current.focus === 'function') current.focus({ preventScroll: true });
    });
  }

  document.addEventListener('submit', function (event) {
    var form = event.target.closest && event.target.closest('[data-bn-discount-form]');
    if (!form) return;
    event.preventDefault();
    if (event.stopImmediatePropagation) event.stopImmediatePropagation();
    var component = form.closest('[data-bn-discount]');
    var input = form.querySelector('[data-bn-discount-input]');
    if (component && input) performDiscountAction(component, input.value, false);
  });

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-bn-discount-remove]');
    if (!button) return;
    event.preventDefault();
    var component = button.closest('[data-bn-discount]');
    if (component) performDiscountAction(component, button.getAttribute('data-bn-discount-remove'), true);
  });

  function initializeDiscounts() {
    if (!document.querySelector('[data-bn-discount]')) return;
    readCart().then(renderDiscountState).catch(function () {});
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeDiscounts, { once: true });
  else initializeDiscounts();

  document.addEventListener('cart:update', function (event) {
    var cart = event.detail && event.detail.resource;
    var data = event.detail && event.detail.data;
    // Native adds invalidate before creating this shared read. Invalidating it
    // again here would discard the current request and force a duplicate GET.
    var cartPromise = data && data.source === 'product-form-component' && data.confirmedAdd === true && data.cartPromise;
    if (!cartPromise) invalidateCartRead(cart);
    Promise.resolve().then(function () { return settleCartEvent(event); }).then(function () {
      if (cartPromise) return cartPromise.then(renderDiscountState);
      if (cart && Number.isInteger(cart.item_count) && Array.isArray(cart.items)) renderDiscountState();
      else if (document.querySelector('[data-bn-discount]')) readCart().then(renderDiscountState).catch(function () {});
    }).catch(function () {});
  });

  function markCartRestoring(restoring) {
    gateAcceleratedCheckout();
    var drawer = document.querySelector('cart-drawer-component');
    if (!drawer) return;
    drawer.inert = restoring;
    var dialog = drawer.querySelector('dialog');
    if (dialog && dialog.style) {
      if (restoring) dialog.style.setProperty('visibility', 'hidden');
      else dialog.style.removeProperty('visibility');
    }
  }

  function refreshCart() {
    if (refreshPending) return refreshPending;
    markCartRestoring(true);
    var pending = enqueueMutation(function () {
      invalidateCartRead();
      var plan = cartSectionPlan();
      return settleRequiredSections({ sections: {} }, plan, { recoverHistorySections: true })
        .then(function (body) { return reconcileGifts(body); })
        .then(function (body) {
          return publishCartResponse(body, { source: 'cart-history', mutation: 'update' });
        })
        .then(function () {
          needsRestore = false;
          markCartRestoring(false);
        })
        .catch(function (error) {
          var drawer = document.querySelector('cart-drawer-component');
          var closing = drawer && typeof drawer.closeAfterRestoreFailure === 'function'
            ? drawer.closeAfterRestoreFailure() : Promise.resolve();
          return Promise.resolve(closing).finally(function () { markCartRestoring(false); }).then(function () { throw error; });
        });
    });
    refreshPending = pending;
    var clear = function () { if (refreshPending === pending) refreshPending = null; };
    pending.then(clear, clear);
    return pending;
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    var submitter = event.submitter || (form.querySelector && form.querySelector('[name="checkout"]'));
    if (checkoutAuthorized || !submitter || submitter.name !== 'checkout') return;
    event.preventDefault();
    if (event.stopImmediatePropagation) event.stopImmediatePropagation();
    continueCheckout(function () { submitCurrentCheckout(form, submitter); });
  }, true);

  document.addEventListener('click', function (event) {
    var link = event.target.closest('a[href]');
    if (!link || !/(^|\/)checkout(?:[/?#]|$)/.test(link.getAttribute('href') || '')) return;
    event.preventDefault();
    if (event.stopImmediatePropagation) event.stopImmediatePropagation();
    continueCheckout(function () { window.location.assign(link.href); });
  }, true);

  function removeDisabledGifts() {
    initializeCartLineState();
    if (giftsEnabled() || !lastCartItems.some(isMarkedGift)) return;
    enqueueMutation(function () {
      invalidateCartRead();
      return readCart().then(function (cart) { return reconcileGifts(cart); })
        .then(function (cart) { return publishCartResponse(cart, { source: 'cart-gift-reconciliation', mutation: 'update' }); });
    }).catch(function (error) { console.error('[bn-quickcart disabled gift cleanup]', error); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', removeDisabledGifts, { once: true });
  else removeDisabledGifts();

  if (typeof MutationObserver === 'function' && (document.documentElement || document.body)) {
    var walletObserver = new MutationObserver(function () {
      if (pendingMutations > 0 || needsRestore || giftDiscountProblem) gateAcceleratedCheckout();
    });
    walletObserver.observe(document.documentElement || document.body, { childList: true, subtree: true });
  }

  if (typeof window.addEventListener === 'function') {
    window.addEventListener('pagehide', function () {
      needsRestore = true;
      markCartRestoring(true);
    });
    window.addEventListener('pageshow', function (event) {
      var navigation = window.performance && window.performance.getEntriesByType('navigation')[0];
      if (!event.persisted && (!navigation || navigation.type !== 'back_forward') && !needsRestore) return;
      needsRestore = true;
      refreshCart().catch(function (error) { console.error('[bn-quickcart history]', error); });
    });
  }

  function setCrossSellStatus(btn, message, isError) {
    var drawer = btn.closest('cart-drawer-component');
    var status = drawer && drawer.querySelector('[data-bnc-status]');
    if (!status) return;
    status.textContent = message || '';
    status.classList.toggle('is-error', Boolean(isError));
  }

  function preserveCrossSellStatus(message) {
    var drawer = document.querySelector('cart-drawer-component');
    if (!drawer || typeof MutationObserver === 'undefined') return;
    var completed = false;
    var timer = 0;

    function restore() {
      var status = drawer.querySelector('[data-bnc-status]');
      if (!status) return false;
      status.textContent = message;
      status.classList.remove('is-error');
      var track = drawer.querySelector('[data-bnc-xsell-track]');
      if (track) {
        track.scrollLeft = 0;
        updateCrossSellNav(track);
      }
      return true;
    }

    function finishRestore() {
      if (completed) return;
      if (!restore()) return;
      completed = true;
      observer.disconnect();
      if (timer) clearTimeout(timer);
    }

    var observer = new MutationObserver(function () {
      finishRestore();
    });

    observer.observe(drawer, { childList: true, subtree: true });
    timer = setTimeout(function () {
      if (completed) return;
      restore();
      completed = true;
      observer.disconnect();
    }, 2500);
  }

  function updateCrossSellNav(track) {
    var crossSell = track.closest('.bnc-xsell');
    var cards = track.querySelectorAll('[data-bnc-xsell-card]');
    if (!crossSell || !cards.length) return;
    var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
    var step = cards[0].getBoundingClientRect().width + gap;
    var index = Math.max(0, Math.min(cards.length - 1, Math.round(track.scrollLeft / step)));
    var current = crossSell.querySelector('[data-bnc-xsell-current]');
    var previous = crossSell.querySelector('[data-bnc-xsell-prev]');
    var next = crossSell.querySelector('[data-bnc-xsell-next]');
    if (current && current.textContent !== String(index + 1)) {
      current.textContent = String(index + 1);
    }
    if (previous) previous.disabled = index === 0;
    if (next) next.disabled = index === cards.length - 1;
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-bnc-add]');
    if (!btn || btn.disabled) return;
    e.preventDefault();

    var id = btn.getAttribute('data-bnc-add');
    if (!id) return;

    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
    btn.disabled = true;
    setCrossSellStatus(btn, '', false);

    var addedMessage = btn.getAttribute('data-bnc-added-message') || 'Added';
    var productName = btn.getAttribute('data-bnc-product-name') || '';
    var successMessage = addedMessage + (productName ? ': ' + productName : '');

    window.BionoboCartMutations.add(
      { id: Number(id), quantity: 1 },
      {
        source: 'cart-recommendation',
        sourceId: id,
        errorMessage: btn.getAttribute('data-bnc-error-message') || 'We could not add this item to your cart. Please try again.',
        beforePublish: function () {
          setCrossSellStatus(btn, successMessage, false);
          preserveCrossSellStatus(successMessage);
        },
      }
    )
      .then(function () {
        var label = btn.querySelector('.bnc-xadd-label');
        btn.classList.remove('is-loading');
        btn.classList.add('is-added');
        btn.removeAttribute('aria-busy');
        if (label) label.textContent = addedMessage.toUpperCase();
      })
      .catch(function (err) {
        console.error('[bn-quickcart]', err);
        btn.classList.remove('is-loading');
        btn.removeAttribute('aria-busy');
        btn.disabled = false;
        setCrossSellStatus(btn, btn.getAttribute('data-bnc-error-message'), true);
      });
  });

  document.addEventListener('click', function (e) {
    var arrow = e.target.closest('[data-bnc-xsell-prev], [data-bnc-xsell-next]');
    if (!arrow || arrow.disabled) return;
    var crossSell = arrow.closest('.bnc-xsell');
    var track = crossSell && crossSell.querySelector('[data-bnc-xsell-track]');
    var card = track && track.querySelector('[data-bnc-xsell-card]');
    if (!track || !card) return;
    var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
    var direction = arrow.hasAttribute('data-bnc-xsell-prev') ? -1 : 1;
    track.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior: 'smooth' });
  });

  document.addEventListener('scroll', function (e) {
    var track = e.target;
    if (!track.matches || !track.matches('[data-bnc-xsell-track]')) return;
    cancelAnimationFrame(track.__bnCrossSellFrame);
    track.__bnCrossSellFrame = requestAnimationFrame(function () {
      updateCrossSellNav(track);
    });
  }, true);

  /* Free gift picker. Same add path as the cross-sell, with one extra step: swapping
   * removes the gift already in the basket first, otherwise the customer ends up holding
   * two and the discount only ever zeroes one of them. */
  var giftSelectionPending = false;
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-bn-cgift-add]');
    if (!btn || btn.disabled || giftSelectionPending) return;
    if (!giftsEnabled()) { e.preventDefault(); return; }
    e.preventDefault();

    var id = btn.getAttribute('data-bn-cgift-add');
    if (!id) return;
    var isSwap = btn.hasAttribute('data-bn-cgift-swap');
    var claimedVariantId = btn.getAttribute('data-bn-cgift-claimed-variant');
    var claimedKey = btn.getAttribute('data-bn-cgift-claimed-key');
    var claimedTier = btn.getAttribute('data-bn-cgift-claimed-tier');
    var tier = btn.getAttribute('data-bn-cgift-tier');
    var threshold = Number(btn.getAttribute('data-bn-cgift-threshold'));
    var giftItem = { id: Number(id), quantity: 1 };
    if (tier && Number.isInteger(threshold) && threshold > 0) {
      giftItem.properties = { _bn_gift: '1', _bn_gift_tier: tier, _bn_gift_threshold: String(threshold) };
    }
    var picker = btn.closest('[data-bn-cgift]');
    var choices = picker ? Array.prototype.slice.call(picker.querySelectorAll('[data-bn-cgift-add]')) : [btn];
    giftSelectionPending = true;
    choices.forEach(function (choice) {
      choice.disabled = true;
      choice.setAttribute('aria-busy', 'true');
    });
    btn.classList.add('is-loading');

    window.BionoboCartMutations.add(
      giftItem,
      {
        source: 'cart-gift',
        sourceId: id,
        errorMessage: 'We could not add this gift to your cart. Please try again.',
        beforeRequest: giftItem.properties ? function () {
          if (!giftsEnabled()) throw new Error('Gift rewards are currently unavailable.');
          invalidateCartRead();
          return readCart().then(function (cart) { return reconcileGifts(cart); }).then(function (cart) {
            if (giftEligibleCents(cart) < threshold) {
              var staleError = new Error('Your gift eligibility changed. Please choose again.');
              staleError.cartMutationUncertain = true;
              throw staleError;
            }
            if (!isSwap && cart.items.some(isMarkedGift)) {
              var claimedError = new Error('Your gift selection changed. Please choose again.');
              claimedError.cartMutationUncertain = true;
              throw claimedError;
            }
            return isSwap ? removeClaimedGift(claimedVariantId, claimedKey, claimedTier, cart) : cart;
          });
        } : (isSwap ? function () { return removeClaimedGift(claimedVariantId, claimedKey); } : null),
      }
    )
      .then(function () {
        btn.classList.remove('is-loading');
      })
      .catch(function (err) {
        console.error('[bn-quickcart gift]', err);
        showGiftNotice(giftStateFromResponse(), 'updateFailed');
        btn.classList.remove('is-loading');
        choices.forEach(function (choice) {
          choice.disabled = false;
          choice.removeAttribute('aria-busy');
        });
      })
      .finally(function () {
        giftSelectionPending = false;
      });
  });

  /* Locate the claimed gift again after entering the mutation queue. Other free lines
   * can coexist in the cart and must never be removed by a gift swap. */
  function removeClaimedGift(claimedVariantId, claimedKey, tier, currentCart) {
    if (!giftsEnabled() || !claimedVariantId || !claimedKey) return Promise.reject(new Error('The claimed gift could not be identified.'));
    if (claimedKey) claimedKey = resolveLineKey(claimedKey);
    if (!currentCart) invalidateCartRead();
    return (currentCart ? Promise.resolve(currentCart) : readCart())
      .then(function (cart) {
        if (!Array.isArray(cart.items)) throw new Error('The cart could not be read. Please try again.');
        var claimed = cart.items.find(function (line) {
          if (claimedKey && line.key !== claimedKey) return false;
          if (String(line.variant_id) !== claimedVariantId) return false;
          if (tier && line.properties && line.properties._bn_gift_tier
            && line.properties._bn_gift_tier !== tier) return false;
          return isMarkedGift(line);
        });
        // A stale drawer may identify a gift that is no longer free. Refresh it
        // before another add; the current cart may contain a different reward.
        if (!claimed) {
          var staleError = new Error('Your gift selection changed. Please choose again.');
          staleError.cartMutationUncertain = true;
          throw staleError;
        }
        var updates = {};
        var sectionPlan = cartSectionPlan();
        updates[claimed.key] = 0;
        return fetch(cartUrl('update.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            updates: updates,
            sections: sectionPlan.requested.join(','),
            sections_url: window.location.pathname,
          }),
        }).then(function (response) {
          return response.json().catch(function () {
            var error = new Error('The gift removal could not be confirmed. Please refresh your cart.');
            error.cartMutationUncertain = true;
            throw error;
          }).then(function (body) {
            // /cart/update.js returns the entire resulting cart, unlike /cart/add.js.
            var removed = Array.isArray(body && body.items) && !body.items.some(function (line) {
              return line.key === claimed.key;
            });
            if (!response.ok || (body && body.status) || !removed) {
              var error = new Error((body && (body.description || body.message)) || 'We could not replace this gift. Please try again.');
              error.cartMutationUncertain = true;
              throw error;
            }
            invalidateCartRead(body);
            return settleRequiredSections(body, sectionPlan);
          });
        });
      });
  }

})();
